import { visualPriority as acentoPersona, locationFirst, eventSentence } from "@/components/visualV2/presentation";
// Zona 3 — Caso activo Human-First (Central Galarya V3).
// Jerarquía: QUÉ pasó (verbo) → A QUIÉN (persona) → DÓNDE → cronómetro → SIGUIENTE ACCIÓN → protocolo.
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { tipoMeta, ESTADO_LABEL, RESULTADO_LABEL, CAPACIDAD_LABEL } from "@/lib/alarmMeta";
import { calcEdad, fmtTimestamp } from "@/lib/format";
import { verboEvento, verboEventoInst, iconoEvento, esTecnica, siguienteAccion, elapsedHuman, origenDispositivo } from "@/lib/consolaV3/meta";
import { lecturaEkhEvento } from "@/lib/consolaV3/ekhEvento";
import RegistrarResultadoModal, { RESULTADO_GESTION, MOTIVO_GESTION } from "@/components/consolaV3/RegistrarResultadoModal";
import AudioTwoWay, { RAZON_NO_AUDIO, MOTIVOS_AUDIO_NO_INSTALADO } from "@/components/consolaV3/AudioTwoWay";
import SiguienteAccionControl from "@/components/consolaV3/SiguienteAccionControl";
import HabitacionCompartida from "@/components/consolaV3/HabitacionCompartida";
import ClipEvento from "@/components/eventos/ClipEvento";
import DesenlaceDirectoControl from "@/components/consolaV3/DesenlaceDirectoControl";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { BotonesReporteIncidente } from "@/components/reportes/ReporteControles";
import {
  Phone, Copy, MapPin, ArrowRightCircle, ListChecks, ShieldAlert,
  CircleAlert, Bell, UserRound, UserCog, History, ImageOff, ScanLine, Wrench, Check, StickyNote, HeartPulse, Wind, BatteryWarning,
} from "lucide-react";

function Evidencia({ detail, demo }) {
  const [estado, setEstado] = useState("cargando");
  const [url, setUrl] = useState(null);
  const fuente = detail._fuente;
  const evId = detail.evento_disparador_id;

  useEffect(() => {
    if (demo) {
      if (fuente === "hm" && detail._termica) { setUrl(detail._termica); setEstado("ok"); }
      else setEstado("no_disponible");
      return;
    }
    if (!evId) { setEstado("no_disponible"); return; }
    let obj = null, vivo = true;
    setEstado("cargando"); setUrl(null);
    api.get(`/eventos/${evId}/imagen`, { responseType: "blob" })
      .then((r) => { if (!vivo) return; obj = URL.createObjectURL(r.data); setUrl(obj); setEstado("ok"); })
      .catch(async (e) => {
        if (!vivo) return;
        let est = "no_disponible";
        try { const t = e?.response?.data ? await e.response.data.text() : null; if (t) est = JSON.parse(t).estado || "no_disponible"; }
        catch (_) { est = e?.response?.status === 404 ? "no_disponible" : "fallo"; }
        setEstado(est);
      });
    return () => { vivo = false; if (obj) URL.revokeObjectURL(obj); };
  }, [evId, demo, fuente, detail._termica]);

  return (
    <section data-testid="v3-evidencia" className="rounded-xl border border-slate-200 bg-white dark:bg-[#14243A] p-4">
      <h3 className="mb-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#012472] dark:text-[#80B3FF]">
        <ScanLine strokeWidth={1.8} className="h-4 w-4" /> Evidencia
      </h3>
      {/* CLIPS-HM-01 · imagen y clip son la MISMA evidencia del evento: al lado en escritorio,
          uno debajo del otro en ancho pequeño. La imagen conserva su recorrido actual. */}
      <div className="grid gap-4 md:grid-cols-[minmax(0,auto)_minmax(0,1fr)] md:items-start">
      {estado === "ok" ? (
        <img src={url} alt="Silueta térmica anonimizada del evento" data-testid="v3-evidencia-img" onError={() => setEstado("fallo")}
          className="max-h-56 rounded-lg border border-slate-200" />
      ) : (
        <div data-testid={`v3-evidencia-${estado}`}
          className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center">
          {estado === "cargando" ? <ScanLine className="h-5 w-5 text-slate-400 animate-pulse" />
            : (fuente === "ekt" || estado === "no_disponible") ? <ImageOff className="h-5 w-5 text-slate-400" />
            : <ImageOff className="h-5 w-5 text-slate-400" />}
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {estado === "cargando" ? "Cargando evidencia…"
              : estado === "expirada" ? "Imagen expirada por política de retención (7 días)"
              : estado === "fallo" ? "No se pudo cargar la imagen"
              : "Sin imagen para este evento"}
          </p>
          {/* C (cierre final) · la leyenda de origen sólo se afirma cuando el transporte ES radar
              (EKT, sin cámara). Antes se mostraba también para cualquier «no disponible», lo que
              atribuía a radar eventos de otras fuentes. */}
          {fuente === "ekt" && estado !== "cargando" && (
            <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-600">Origen: radar (sin cámara)</p>
          )}
        </div>
      )}
      {evId && <ClipEvento eventoId={evId} demo={demo} />}
      </div>
    </section>
  );
}

function CallBtns({ telefono, idx, nombre, bloqueado = false, secundario = false, onIniciar }) {
  if (!telefono) return <span className="text-sm text-slate-400">Sin teléfono</span>;
  const quien = nombre ? ` a ${nombre}` : "";
  // v2.13.4 — ANTES DE TOMAR la alarma el número se muestra como CONTEXTO, pero no se puede
  // iniciar la llamada: la verificación pertenece a quien ya es dueño del caso.
  if (bloqueado) {
    return (
      <span data-testid={`v3-llamar-bloqueado-${idx}`} className="text-xs text-slate-400">
        Toma la alarma para llamar
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <a data-testid={`v3-llamar-${idx}`} href={`tel:${telefono.replace(/\s+/g, "")}`}
        aria-label={`Llamar${quien} al ${telefono}`}
        onClick={() => { if (onIniciar) onIniciar(); }}
        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#012472] ${secundario
          ? "border border-[#012472]/30 dark:border-[#80B3FF]/40 text-[#012472] dark:text-[#80B3FF] hover:bg-[#012472]/5"
          : "bg-[#01CBE3] text-[#012472] hover:bg-[#01B5CB]"}`}>
        <Phone strokeWidth={1.9} className="h-4 w-4" /> Llamar al teléfono
      </a>
      <button data-testid={`v3-copiar-${idx}`} aria-label={`Copiar el teléfono${quien}`}
        onClick={() => { navigator.clipboard?.writeText(telefono); toast.success("Número copiado"); }}
        className="inline-flex items-center rounded-lg border border-slate-200 px-2.5 py-1.5 text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#192C47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#012472]">
        <Copy strokeWidth={1.7} className="h-4 w-4" /><span className="sr-only">Copiar teléfono</span>
      </button>
    </div>
  );
}

function Section({ icon: Icon, title, children, testId, tone = "text-[#012472] dark:text-[#80B3FF]" }) {
  return (
    <section data-testid={testId} className="rounded-xl border border-slate-200 bg-white dark:bg-[#14243A] p-4">
      <h3 className={`mb-2.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${tone}`}>
        <Icon strokeWidth={1.8} className="h-4 w-4" /> {title}
      </h3>
      {children}
    </section>
  );
}

function NotaGestionV3({ onGuardar, busy, valor, onCambio }) {
  const [local, setLocal] = useState("");
  const txt = onCambio ? (valor || "") : local;
  const setTxt = onCambio || setLocal;
  const guardar = async () => {
    const t = txt.trim();
    if (!t) return;
    const r = await onGuardar(t);
    // ATN-01 · M6.1 — el campo sólo se limpia con un resultado CONFIRMADO del guardado.
    if (!r || r.estado === undefined || String(r.estado).startsWith("confirmado")) setTxt("");
  };
  return (
    <div>
      <textarea data-testid="v3-input-nota-gestion" value={txt} onChange={(e) => setTxt(e.target.value)} rows={2}
        placeholder="Registra la acción tomada (llamada al técnico, visita, confirmación). Queda en el registro."
        className="w-full rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 dark:text-slate-200" />
      <div className="mt-2">
        <Button data-testid="v3-btn-guardar-nota" onClick={guardar} disabled={busy || !txt.trim()} className="bg-[#012472] hover:bg-[#012472]/90 text-white font-semibold">Guardar nota</Button>
      </div>
    </div>
  );
}

// H2: etiquetas del release_reason_code (catálogo cerrado del backend). 'inactividad_sesion' es
// sólo del sistema (liberación automática), por eso no se ofrece en el menú manual.
const MOTIVO_LIBERACION_LABEL = {
  handoff: "Traspaso a otro operador", fin_turno: "Fin de turno",
  toma_erronea: "Tomada por error", otro: "Otro motivo",
};

// H2: reconstrucción autoritativa de la secuencia del caso desde `transiciones[]` (backend).
// Read-only; sirve tanto a alarmas de persona como a incidencias técnicas (persona_id=null).
const SEQ_ESTADO_LABEL = {
  tomada: "Tomada", en_verificacion: "En verificación",
  no_verificable: "No verificable", escalada: "Escalada", cerrada: "Cerrada",
};
function etiquetaTransicion(t) {
  const est = t.estado;
  if (est === "recibida") {
    // Sin `desde` = recepción inicial; con `desde` = liberación de vuelta a la cola.
    if (!t.desde) return { label: "Recibida", detalle: null };
    return { label: "Liberada", detalle: t.motivo_codigo ? (MOTIVO_LIBERACION_LABEL[t.motivo_codigo] || t.motivo_codigo) : (t.nota || null) };
  }
  if (est === "cerrada") {
    const sistema = t.usuario === "sistema";
    return { label: sistema ? "Cierre del sistema" : "Cerrada",
             detalle: t.clasificacion ? (RESULTADO_LABEL[t.clasificacion] || t.clasificacion.replace(/_/g, " ")) : null };
  }
  return { label: SEQ_ESTADO_LABEL[est] || ESTADO_LABEL[est] || est, detalle: t.nota || null };
}

// v2.9.1: etiquetas legibles para el read-model `secuencia_combinada` del backend.
// GC94 · R02 — etiquetas del estado de apoyo consignado en un escalamiento interno (mismas claves
// cerradas que publica el backend en `estados_apoyo`).
const ESTADO_APOYO_LABEL = {
  solicitado: "Apoyo solicitado", pendiente: "Apoyo pendiente de llegar",
  recibido: "Apoyo recibido en la ubicación", no_obtenido: "Apoyo no obtenido",
};

const SECUENCIA_LABEL = {  recibido: "Recibida", tomado: "Tomada", liberado: "Liberada", cierre: "Cierre del caso",
  audio_started: "Audio iniciado con la persona", audio_ended: "Audio finalizado",
  audio_failed: "Audio fallido", wait_creada: "Espera creada", wait_vencida: "Espera vencida",
  wait_cumplida: "Espera cumplida", retrigger: "Nuevo evento durante el caso (retrigger)",
  solicitud_emergencia: "Solicitud de servicios de emergencia", emergencia_handoff: "Handoff a emergencia",
  seguimiento_emergencia: "Seguimiento post-emergencia",
  journal_audio_persona: "Resultado de audio con la persona",
  journal_llamada_persona: "Llamada a la persona", journal_llamada_contacto: "Llamada a contacto",
  journal_aviso_contacto: "Aviso a contacto", journal_solicitud_emergencia: "Solicitud de emergencia",
  journal_confirmacion_tercero: "Confirmación de tercero", journal_reintento: "Reintento",
  journal_accion_final: "Acción final del plan ejecutada",
  // Hechos que también aparecen en los escenarios demo (el backend no los emite con estas claves).
  verificacion_desenlace: "Desenlace registrado", paso_ejecutado: "Paso del plan ejecutado",
  gestion_contacto: "Resultado de contacto registrado", journal_nota: "Nota de la gestión",
  transicion_en_verificacion: "En verificación", transicion_no_verificable: "No verificable",
  transicion_escalada: "Escalada",
  escalamiento_interno: "Escalamiento al personal del centro",
  journal_verificacion_persona: "Verificación del estado de la persona",
  evidencia_dispositivo: "Evidencia del dispositivo",
  journal_evidencia_dispositivo: "Evidencia del dispositivo",
};

// CONSOLA-05A (AUD-002/AUD-003): la etiqueta se DERIVA del hecho estructurado (clase + canal), no
// del nombre del kind. Así una gestión de contacto nunca se lee como "audio" y una lectura del
// sensor se describe como evidencia del dispositivo. Histórico sin clase/canal: cae al mapa fijo.
const CANAL_LABEL = {
  audio: "Resultado de audio con la persona",
  telefono_persona: "Resultado de la llamada a la persona",
  telefono_contacto: "Resultado del contacto (llamada)",
  presencial: "Gestión presencial registrada",
  mensaje: "Mensaje registrado",
};
function etiquetaHecho(it) {
  if (it.clase === "evidencia") return "Evidencia del dispositivo";
  // OPS-01 §6/§7 — una acción INICIADA no se lee como resultado: se nombra como intento.
  if (it.fase === "iniciada" && it.tipo === "journal_llamada_persona") return "Intento de llamada a la persona (iniciado)";
  if (it.fase === "iniciada" && it.tipo === "journal_llamada_contacto") return "Intento de llamada a contacto (iniciado)";
  if (it.canal && CANAL_LABEL[it.canal] && String(it.tipo).startsWith("journal_")) return CANAL_LABEL[it.canal];
  if (it.tipo === "gestion_contacto") return CANAL_LABEL.telefono_contacto;
  return SECUENCIA_LABEL[it.tipo] || it.tipo;
}

export default function CaseDetailV3({ detail, nowMs, busy, demo, onTake, onCloseClick, onLiberar, onSolicitarApoyo,
  onTransition, onAtenderTambien, takeBlockedAlta, onNotaGestion, onRegistrarGestion, onCumplirWait,
  onRegistrarAccion, onRegistrarDesenlace, onResolverSeguimiento, onRegistrarEscalamiento, onIntentoLlamada,
  readOnly = false, motivosLiberacion = [], isSupervisor = false, permitirReporteIncidente = false,
  onIdentificarResidente, drafts = null, actorId = null }) {
  const a = detail;
  const [gestionFor, setGestionFor] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [notaOpen, setNotaOpen] = useState(false);
  // A11Y-004: el diálogo de gestión se controla por estado (sin DialogTrigger), así que Radix no
  // conoce el control que lo abrió y el foco caía en <body> al cerrarlo (Cancelar, Escape y X).
  // Se recuerda el disparador y se le devuelve el foco; si ya no está (caso cerrado o relevo),
  // el foco va a la sección de contactos, que es el destino lógico para seguir por teclado.
  const disparadorGestionRef = useRef(null);
  const devolverFocoGestion = () => {
    setTimeout(() => {
      const b = disparadorGestionRef.current;
      if (b && b.isConnected) { b.focus(); return; }
      const seccion = document.querySelector("[data-testid=v3-contactos]");
      if (seccion) { seccion.setAttribute("tabindex", "-1"); seccion.focus(); }
    }, 0);
  };
  const [desenlaceOpen, setDesenlaceOpen] = useState(false);
  // v2.9.10 — el fallback de desenlace sólo se abre por acción explícita (o kind==='desenlace');
  // al cambiar de caso se resetea para que NO herede el estado abierto del caso anterior.
  useEffect(() => { setDesenlaceOpen(false); }, [detail?.id]);
  const RES_LBL = Object.fromEntries(RESULTADO_GESTION.map((r) => [r.v, r.l]));
  const MOTIVO_LBL = Object.fromEntries(MOTIVO_GESTION.map((m) => [m.v, m.l]));
  const gestiones = a.gestiones_contacto || [];
  const tec = esTecnica(a);
  const Icon = iconoEvento(a.tipo);
  const isRecibida = a.estado === "recibida";
  const isCerrada = a.estado === "cerrada";
  const ac = acentoPersona(a.prioridad);
  const persona = a.persona || {};
  const sitio = a.sitio || {};
  const edad = calcEdad(persona.fecha_nacimiento);
  const donde = locationFirst(a.zona_nombre, a.sitio_nombre || sitio.nombre);
  const pasos = (a.action_plan_congelado || {}).pasos || [];
  const contactos = a.contactos_resueltos || [];
  const rec = a.recursos_disponibles || {};
  // UI-01A · CAPACIDAD instalada vs DISPONIBILIDAD de la sesión. `recursos_disponibles.audio` es el
  // resultado del MISMO preflight que gobierna el control de audio, así que un "no" puede ser
  // temporal (caso sin tomar, equipo ocupado, conexión sin confirmar) y decir "no disponible en
  // este sitio" era contradictorio con el botón «Hablar por dispositivo». Se presenta el motivo
  // real; la lógica de audio, el preflight y la autorización NO se tocan.
  const audioMotivo = rec.audio_motivo || null;
  const audioRazon = audioMotivo ? (RAZON_NO_AUDIO[audioMotivo] || "") : "";
  const audioTxt = !audioMotivo
    ? "no disponible ahora."
    : MOTIVOS_AUDIO_NO_INSTALADO.has(audioMotivo)
      ? `no configurada para este caso. ${audioRazon}`
      : `instalada, pero no disponible ahora. ${audioRazon}`;
  // C (presentación, sin dato nuevo): el resultado de una llamada a un contacto se persiste en el
  // JOURNAL (`journal_llamada_contacto`/`journal_aviso_contacto`, con el contacto en `ref`),
  // mientras `gestiones_contacto` sólo recoge la gestión manual de Gate B. El panel leía ÚNICAMENTE
  // lo segundo y por eso un contacto con resultado registrado se presentaba «Sin gestión
  // registrada». Aquí se combinan los DOS hechos ya existentes y gana el más reciente.
  const hechosJournalContacto = (a.secuencia_combinada || []).filter(
    (it) => it.ref && (it.tipo === "journal_llamada_contacto" || it.tipo === "journal_aviso_contacto"));
  const ultimoHechoContacto = (cid) => [
    ...gestiones.filter((g) => g.contacto_id === cid).map((g) => ({ fuente: "gestion", hora: g.registrado_en, g })),
    ...hechosJournalContacto.filter((it) => it.ref === cid).map((it) => ({ fuente: "journal", hora: it.hora, it })),
  ].filter((x) => x.hora).sort((x, y) => String(x.hora).localeCompare(String(y.hora))).slice(-1)[0];
  // Identidad del contacto para la secuencia: del snapshot congelado del caso o de los contactos
  // resueltos. NO se consulta nada nuevo ni se añade PII que la pantalla no muestre ya.
  const identidadContacto = (cid) => {
    const r = contactos.find((x) => (x.contacto_id || x.contacto?.id) === cid);
    if (r) return [r.contacto?.nombre_completo, r.relacion_familiar].filter(Boolean).join(" · ");
    const s = ((a.action_plan_congelado || {}).contactos_snapshot || []).find((x) => x.contacto_id === cid);
    return s ? [s.nombre, s.relacion].filter(Boolean).join(" · ") : null;
  };
  // ATN-01 · M3.3 — la FAMILIA se identifica por el tipo del caso, nunca por la existencia del
  // subobjeto de contexto: si el detalle del recordatorio no llega, sigue siendo un recordatorio y
  // no se activan audio ni «otra vía» como verificación de salud.
  const esRecordatorio = a.tipo === "recordatorio_programado" || !!a.recordatorio;
  // ATN-01 · M2 — la etiqueta del código se toma del CATÁLOGO que ya publica el caso; sólo si el
  // código no está en él se recurre al mapa local. Nunca se inventa texto.
  const etiquetaResultado = (cod) => {
    if (!cod) return "";
    const cat = [...(a.resultados_permitidos || []), ...(a.cierres_permitidos || []),
                 ...(a.resoluciones_tecnicas || [])].find((r) => r.codigo === cod);
    return (cat && (cat.etiqueta || cat.label)) || RESULTADO_LABEL[cod] || String(cod).replace(/_/g, " ");
  };
  const atrib = a.atribucion;
  const personaLabel = persona.nombre_completo || a.persona_nombre
    || (atrib === "compartida" ? "No atribuible individualmente · habitación compartida"
      : atrib === "sin_asignar" ? "Dispositivo sin persona asignada"
      : "Persona no identificada");
  const cui = a.cuidadora_en_turno;
  // MODALIDAD: AUTORITATIVA del backend (`modalidad_respuesta` del read-model). El DEMO
  // determinista conserva su propia semántica DEMO (`_modalidad`). El frontend REAL ya NO deduce la
  // modalidad del sitio ni traduce semántica: sólo renderiza.
  const instMode = (a.modalidad_respuesta || a._modalidad) === "institucional";
  // UI-01C: el detalle usa la MISMA etiqueta de presentación que la cola, el Radar y la Bitácora.
  const titulo = a.tipo === "cobertura_perdida" && a.capacidad ? `${verboEvento(a.tipo)} · ${(CAPACIDAD_LABEL[a.capacidad] || a.capacidad).toUpperCase()}`
    : instMode ? verboEventoInst(a.tipo) : verboEvento(a.tipo);
  const accion = a.siguiente_accion?.etiqueta || siguienteAccion(a);
  const waitsActivas = (a.waits || []).filter((w) => w.status === "activa" || w.status === "vencida");
  // OPS-01 §14 — el contexto de repeticiones lo deriva el BACKEND (contexto vs. reverificación).
  const rt = a.retrigger_contexto || {};
  const hayRetrigger = (rt.total || 0) > 0;
  const reverificar = !!rt.amerita_reverificacion;
  const estadoTxt = isRecibida ? "Sin tomar" : isCerrada ? "Cerrada"
    : a.estado === "tomada" ? "En atención" : a.estado === "en_verificacion" ? "En verificación" : (ESTADO_LABEL[a.estado] || a.estado);
  const esApoyo = !tec && (a.prioridad === "alta" || a.prioridad === "maxima");
  // v2.9.1: la operación normal del monitorista se rige por capacidades AUTORITATIVAS del backend.
  const permitidas = a.acciones_permitidas || [];
  const rutaIncompleta = a.ruta_incompleta;
  const planIncompleto = a.plan_incompleto;
  const puedeCerrar = permitidas.includes("cerrar");

  const headerBorder = "gv2-card";
  const timerTone = "gv2-timer";

  return (
    <div className="space-y-3.5" data-testid="v3-case-detail" data-priority={ac.key}>
      {/* IND-01 (D5) — EQUIPO FÍSICO que originó el caso. Cada dispositivo responde por su propio
          caso: esta línea dice CUÁL lo reportó. Si el caso es histórico y no tiene el origen
          congelado, se muestra el dato actual del catálogo señalándolo. */}
      {(() => {
        const o = origenDispositivo(a);
        return o ? (
          <p data-testid="v3-origen-dispositivo" className="text-xs text-slate-500 dark:text-slate-400">
            Origen: <span className="font-semibold text-slate-700 dark:text-slate-200">{o.texto}</span>
            {!o.congelado && <span className="ml-1 text-slate-400">(dato actual del catálogo, no congelado en el caso)</span>}
          </p>
        ) : null;
      })()}
      {/* REPORT-01 — reporte individual del incidente (abierto, en atención o cerrado). El PDF lo
          genera el backend con los hechos registrados; un caso sin cerrar se entrega marcado como
          documento PARCIAL. Sólo se ofrece donde se CONSULTA el expediente (Bitácora): durante la
          atención en Central V3 no se imprime ni se descarga nada. */}
      {permitirReporteIncidente && <BotonesReporteIncidente casoId={a.id} demo={demo} />}
      {/* VS373 (R94) — ORIGEN DEL REPORTE e INCERTIDUMBRE. Reutiliza la superficie de avisos del
          caso. Nunca afirma que dos capturas sean la misma detección ni convierte un estado del
          sensor en atención humana. */}
      {a.origen_familia === "VS373" && (
        <div data-testid="v3-vs373-origen" className="rounded-xl border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-[#192C47] px-4 py-2.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Reportado por sensor de radar VS373
          </p>
          {a.vs373?.atribucion_ambigua && (
            <p data-testid="v3-vs373-ambiguo" className="mt-1 text-xs text-slate-600 dark:text-slate-300">
              El dispositivo monitorea a más de una persona: la identidad no se atribuye automáticamente.
            </p>
          )}
        </div>
      )}
      {a.vs373_leyenda && !isCerrada && (
        <div data-testid="v3-vs373-leyenda" className="flex items-start gap-2 rounded-xl border border-[#D99B2B] bg-[#D99B2B]/10 px-4 py-2.5">
          <History strokeWidth={1.8} className="mt-0.5 h-4 w-4 shrink-0 text-[#B07A16]" />
          <p className="text-xs font-semibold text-[#8A5E10] dark:text-[#E9C06A]">{a.vs373_leyenda}</p>
        </div>
      )}
      {/* OPS-01 §14 — REPETICIONES: contexto agrupado vs. evento POSTERIOR a la última
          verificación efectiva (el único que amerita reverificar). Nunca oculta el evento. */}
      {hayRetrigger && !isCerrada && (
        reverificar ? (
          <div data-testid="v3-retrigger-banner" className="flex items-start gap-2 rounded-xl border-2 border-[#FE6A55] bg-[#FE6A55]/10 px-4 py-3">
            <ShieldAlert strokeWidth={2} className="mt-0.5 h-5 w-5 shrink-0 text-[#FE6A55]" />
            <div className="min-w-0">
              <p className="text-sm font-extrabold uppercase tracking-wide text-[#B93B29] dark:text-[#FE6A55]">
                {a.ws101_entrega_conservadora === true ? "Nueva recepción del botón" : rt.ultimo_tipo === "caida" ? "Nueva caída detectada" : "Nuevo evento detectado"} después de la última verificación — {fmtTimestamp(rt.ultimo_en)}
              </p>
              <p className="mt-0.5 text-xs font-semibold text-[#B93B29] dark:text-[#FE6A55]">Recomendación: verificar nuevamente. Puedes seguir usando cualquier vía o cerrar el caso.</p>
            </div>
          </div>
        ) : (
          <div data-testid="v3-retrigger-contexto" className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 dark:bg-[#192C47] px-4 py-2.5">
            <History strokeWidth={1.8} className="h-4 w-4 shrink-0 text-slate-400" />
            <p className="text-xs text-slate-600 dark:text-slate-300">
              {rt.total} {a.ws101_entrega_conservadora === true ? "recepciones adicionales del botón" : `${rt.total === 1 ? "repetición" : "repeticiones"} del mismo evento`}
              {rt.antes_de_tomar > 0 ? ` · ${rt.antes_de_tomar} mientras el caso estaba pendiente` : ""}
              {rt.durante_atencion > 0 ? ` · ${rt.durante_atencion} durante la atención` : ""}
              {rt.ultimo_en ? ` · última ${fmtTimestamp(rt.ultimo_en)}` : ""} · Contexto del caso: la ruta de atención continúa.
            </p>
          </div>
        )
      )}
      {/* Encabezado Human-First */}
      <div className={`rounded-xl border-2 p-5 ${headerBorder}`}>
        <div>
          <div className="min-w-0">
            <div className="gv2-card-head gv2-detail-head">
              <div className="gv2-card-labels">
              <span className="gv2-priority-badge">{ac.key === "alta" ? <CircleAlert aria-hidden="true" /> : <Bell aria-hidden="true" />}{ac.label}</span>
              <span data-testid="v3-estado-badge" className="gv2-state">{estadoTxt}</span>
              <span className="gv2-type">{tec && <Wrench className="h-3.5 w-3.5" />}{tec ? "Incidencia técnica" : "Persona"}</span>
              </div>
              <div className="gv2-time-block">
                <span className="gv2-time-label">Tiempo transcurrido</span>
                <p data-testid="v3-timer" className={`font-mono tabular-nums text-3xl font-extrabold ${timerTone}`}>{elapsedHuman(a.hora_recepcion_servidor, isCerrada && a.cierre_en ? new Date(a.cierre_en).getTime() : nowMs)}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon strokeWidth={1.9} className={`h-8 w-8 shrink-0 ${ac.text}`} />
              <div className="min-w-0">
                {/* QUÉ pasó (verbo hero) */}
                <h1 data-testid="v3-verbo" className="gv2-title text-2xl font-bold leading-tight break-words">{eventSentence(titulo)}</h1>
                {/* GC106 — lectura LITERAL del evento, destacada bajo el título (Central y Bitácora). */}
                {(() => {
                  const ekh = lecturaEkhEvento(a);
                  if (!ekh) return null;
                  return (
                    <div data-testid="v3-ekh-encabezado"
                      className={`mt-2 inline-flex items-center gap-2 rounded-xl px-3 py-2 ${ekh.presente
                        ? "bg-[#012472]/[0.07] dark:bg-[#01CBE3]/[0.12]"
                        : "bg-slate-100 dark:bg-slate-700/40"}`}>
                      {ekh.esResp
                        ? <Wind strokeWidth={2} className={`h-5 w-5 shrink-0 ${ekh.presente ? "text-[#012472] dark:text-[#80B3FF]" : "text-slate-400"}`} aria-hidden="true" />
                        : <HeartPulse strokeWidth={2} className={`h-5 w-5 shrink-0 ${ekh.presente ? "text-[#012472] dark:text-[#80B3FF]" : "text-slate-400"}`} aria-hidden="true" />}
                      <span data-testid="v3-ekh-encabezado-valor"
                        className={`text-xl font-extrabold tabular-nums ${ekh.presente ? "text-[#012472] dark:text-[#80B3FF]" : "text-slate-500 dark:text-slate-300 text-base font-semibold"}`}>
                        {ekh.texto}
                      </span>
                      {ekh.presente && <span className="text-xs text-slate-500 dark:text-slate-400">al momento del evento</span>}
                    </div>
                  );
                })()}
                {/* A QUIÉN */}
                {!tec ? (
                  <p data-testid="v3-persona" className="gv2-info-line mt-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
                    <UserRound aria-hidden="true" /><span>{personaLabel}
                    {edad != null && <span className="whitespace-nowrap text-base font-normal text-slate-500 dark:text-slate-400"> · {edad} años</span>}</span>
                  </p>
                ) : (
                  <p className="mt-1.5 text-lg font-semibold text-slate-700 dark:text-slate-200">{locationFirst(a.zona_nombre, a.sitio_nombre || sitio.nombre)}</p>
                )}
                {/* DÓNDE */}
                {!tec && donde && <p className="gv2-info-line gv2-location mt-1 text-sm text-slate-500 dark:text-[#94A3B8]"><MapPin className="h-3.5 w-3.5" /> {donde}</p>}
                {/* v2.13.3 — TELÉFONO DE LA PERSONA en el contexto del caso. El dato ya viajaba en el
                    read-model (`persona_telefono`) pero sólo se enunciaba como «registrado»: el
                    operador tenía que salir del caso para leerlo. Aquí se muestra y se puede llamar
                    o copiar. Si no hay teléfono, se dice explícitamente. */}
                {!tec && isRecibida && a.persona_telefono && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <p data-testid="v3-persona-telefono" className="flex items-center gap-1.5 text-sm font-mono text-slate-600 dark:text-slate-300">
                      <Phone strokeWidth={1.8} className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                      {a.persona_telefono_display || a.persona_telefono}
                    </p>
                    <CallBtns telefono={a.persona_telefono} idx="persona" nombre={persona.nombre_completo || a.persona_nombre} bloqueado />
                  </div>
                )}
                {!tec && !isRecibida && (
                  <p data-testid="v3-persona-telefono" className="mt-1.5 flex items-center gap-1.5 text-sm font-mono text-slate-600 dark:text-slate-300">
                    <Phone strokeWidth={1.8} className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    {a.persona_telefono_display || a.persona_telefono || "Sin teléfono registrado"}
                  </p>
                )}
              </div>
            </div>
          </div>
          {a.operador_nombre && <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Operador: {a.operador_nombre}</p>}
        </div>

        {/* Acciones */}
        <div className="mt-4 flex flex-wrap gap-2">
          {!readOnly && isRecibida && !takeBlockedAlta && (
            <Button data-testid="v3-btn-tomar" onClick={onTake} disabled={busy} className="bg-[#01CBE3] hover:bg-[#01B5CB] text-[#012472] font-bold">
              {tec ? "Tomar incidencia" : "Tomar alarma"}
            </Button>
          )}
          {!readOnly && isRecibida && takeBlockedAlta && (
            <div data-testid="v3-segunda-alta" className="w-full rounded-lg border border-[#9B2C2C]/40 bg-[#9B2C2C]/10 p-3">
              <p className="text-sm font-semibold text-[#9B2C2C] dark:text-[#F2B8B8]">Ya tienes una alarma ALTA en atención. Puedes pedir apoyo y, como excepción consciente, atender ésta también (entrarás en sobrecarga; queda auditado).</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button data-testid="v3-btn-apoyo" onClick={onSolicitarApoyo} disabled={busy} className="bg-[#012472] hover:bg-[#012472]/90 text-white font-semibold">Solicitar apoyo</Button>
                <Button data-testid="v3-btn-atender-tambien" onClick={onAtenderTambien} disabled={busy} className="bg-[#9B2C2C] hover:bg-[#822424] text-white font-semibold">Atender también · sobrecarga</Button>
              </div>
            </div>
          )}
          {!readOnly && !isRecibida && !isCerrada && (
            <>
              {/* v2.9.1 ROOT 1: NO se renderizan controles H2 legacy (iniciar verificación / marcar
                  no verificable / escalar / apoyo genérico / cierre genérico). Sólo capacidades del backend. */}
              {rutaIncompleta && (
                <div data-testid="v3-ruta-incompleta" className="w-full rounded-lg border-2 border-amber-500 bg-amber-50 dark:bg-amber-900/20 p-3">
                  <p className="text-sm font-bold text-amber-800 dark:text-amber-300">
                    Atribución de identidad no resuelta. No se puede identificar con quién hablar; solicita intervención de un supervisor.
                  </p>
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">No se inventa identidad. Registra la gestión realizada para una salida documentada; el supervisor conserva su salida excepcional.</p>
                </div>
              )}
              {planIncompleto && !rutaIncompleta && (
                <div data-testid="v3-plan-incompleto" className="w-full rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3">
                  <p className="text-sm font-bold text-amber-800 dark:text-amber-300">
                    Plan de respuesta incompleto. Atiende con el protocolo base disponible.
                  </p>
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">Advertencia administrativa: el commissioning debe corregirse. El caso sigue operable.</p>
                </div>
              )}              {/* Liberar: herramienta EXCEPCIONAL de supervisor (ownership v2.8.7 intacto). */}
              {isSupervisor && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button data-testid="v3-btn-liberar" variant="outline" disabled={busy} className="border-amber-400 text-amber-700 dark:text-amber-300 dark:border-amber-600">Liberar</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" data-testid="v3-liberar-motivos">
                    {(motivosLiberacion.filter((m) => m !== "inactividad_sesion")).map((m) => (
                      <DropdownMenuItem key={m} data-testid={`v3-liberar-motivo-${m}`} onClick={() => onLiberar && onLiberar(m)}>
                        {MOTIVO_LIBERACION_LABEL[m] || m}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {puedeCerrar && (
                <Button data-testid="v3-btn-cerrar" variant="outline" onClick={onCloseClick} disabled={busy} className="border-slate-300 text-slate-500 dark:text-slate-400 hover:text-slate-700">Cerrar caso</Button>
              )}
            </>
          )}
          {isCerrada && (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
              <Check className="h-4 w-4" /> Cerrada: {RESULTADO_LABEL[a.clasificacion] || a.clasificacion}
            </span>
          )}
        </div>
      </div>

      {a.tipo === "boton_panico" && a.ws101_entrega_conservadora === true && (
        <section data-testid="ws101-entrega-conservadora" className="rounded-xl border p-4">
          <p className="font-semibold">Solicitud de ayuda del botón WS101</p>
          <p>Cada recepción se conserva. Una recepción adicional puede ser una retransmisión;
            no confirma otra pulsación física. Verificar en el sitio y registrar la atención.
            El cierre es humano.</p>
        </section>
      )}
      {a.tipo === "ws101_procesamiento" && a.ws101_tecnica && (
        <section data-testid="ws101-tecnica" className="rounded-xl border p-4">
          <p className="font-semibold">{a.ws101_tecnica.motivo}</p>
          <p>{a.ws101_tecnica.ubicacion_historica || "Ubicación no acreditada"}</p>
          {a.ws101_tecnica.posible_pulsacion && <p>Posible pulsación. Revisar en el sitio; una recepción repetida puede ser retransmisión.</p>}
          {a.ws101_tecnica.recepciones_posibles > 0 && <p>Recepciones con posible pulsación: {a.ws101_tecnica.recepciones_posibles}</p>}
          {a.ws101_tecnica.ultima_recepcion_en && <p>Última recepción: {fmtTimestamp(a.ws101_tecnica.ultima_recepcion_en)}</p>}
          <p>{a.ws101_tecnica.detectado_en ? fmtTimestamp(a.ws101_tecnica.detectado_en) : "Fecha no disponible"}</p>
          <p>Esta incidencia técnica no acredita la atención de una solicitud de ayuda. Revisar la bitácora; cierre humano.</p>
        </section>
      )}
      {/* MOKO-BAT-01 — PANEL TÉCNICO DE BATERÍA. Muestra el porcentaje y la fecha de la última
          lectura, la indicación operativa del nivel y los hitos del episodio. La recuperación es un
          hecho del EQUIPO: no afirma que una persona haya cargado el botón, y nunca cierra el caso. */}
      {a.tipo === "bateria_baja" && a.bateria_panel && (
        <div data-testid="v3-bateria-panel" className="rounded-xl p-4">
          <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest gv2-priority-text">
            <BatteryWarning className="h-3.5 w-3.5" /> Batería del botón de ayuda
          </p>
          <div className="mt-2 flex flex-wrap items-end gap-x-6 gap-y-2">
            <div>
              <p data-testid="v3-bateria-porcentaje" className="gv2-priority-text font-mono tabular-nums text-4xl font-extrabold leading-none">
                {Number.isInteger(a.bateria_panel.porcentaje) ? `${a.bateria_panel.porcentaje}%` : "Sin lectura"}
              </p>
              <p data-testid="v3-bateria-leido" className="gv2-muted-text mt-1 text-xs">
                {isCerrada ? "Lectura al cierre: " : "Última lectura: "}
                {a.bateria_panel.leido_en ? fmtTimestamp(a.bateria_panel.leido_en) : "No consta"}
              </p>
            </div>
            {a.bateria_panel.nivel_etiqueta && (
              <span data-testid="v3-bateria-nivel" className={`rounded-md px-2.5 py-1 text-xs font-bold ${
                isCerrada ? "gv2-badge-closed"
                : a.bateria_panel.nivel === "inmediata" ? "gv2-badge-priority"
                : a.bateria_panel.nivel === "pronto" ? "gv2-badge-amber"
                : "gv2-badge-good"}`}>
                {isCerrada ? `Nivel al cierre: ${a.bateria_panel.nivel_etiqueta}` : a.bateria_panel.nivel_etiqueta}
              </span>
            )}
          </div>
          {/* Con la incidencia cerrada el panel es HISTÓRICO: no se sigue ordenando una carga que
              ya se resolvió, sólo se conserva lo que el equipo reportó. */}
          {a.bateria_panel.indicacion && !isCerrada && (
            <p data-testid="v3-bateria-indicacion" className="gv2-strong-text mt-2 text-sm font-semibold leading-snug">
              {a.bateria_panel.indicacion}
            </p>
          )}
          {a.bateria_panel.verificacion_pendiente && !isCerrada && (
            <p data-testid="v3-bateria-verificacion" className="gv2-plain gv2-amber-text mt-2 p-2 text-xs font-semibold">
              {a.bateria_panel.verificacion_pendiente === "lectura_contradictoria"
                ? "El equipo reportó una carga menor después de la recuperación. Verifica con el sitio antes de decidir."
                : "El equipo reportó un salto de carga poco plausible. Queda pendiente de una segunda lectura; verifica con el sitio."}
            </p>
          )}
          {(a.bateria_panel.transiciones || []).length > 0 && (
            <ul data-testid="v3-bateria-hitos" className="gv2-divider mt-3 space-y-1 border-t pt-2">
              {a.bateria_panel.transiciones.map((t, i) => (
                <li key={`${t.hora}-${i}`} data-testid={`v3-bateria-hito-${i}`} className="gv2-muted-text flex flex-wrap items-baseline gap-x-2 text-xs">
                  <span className="gv2-strong-text font-semibold">{t.etiqueta}</span>
                  {Number.isInteger(t.porcentaje) && <span className="font-mono tabular-nums">{t.porcentaje}%</span>}
                  <span className="gv2-muted-text">{t.hora ? fmtTimestamp(t.hora) : ""}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="gv2-muted-text mt-2 text-[11px]">
            {isCerrada
              ? "Registro histórico de la batería de esta incidencia. La resolución la decidió una persona."
              : "El cierre de esta incidencia es humano: resolución «Restablecido» con la nota de gestión correspondiente."}
          </p>
        </div>
      )}

      {/* INSTITUCIONAL — indicador DISCRETO de protocolo predeterminado (read-model: no es blocker
          de readiness y no bloquea la operación del centro). */}
      {instMode && a.plan_no_configurado && !isCerrada && (
        <p data-testid="v3-protocolo-default-inst" className="text-xs text-slate-500 dark:text-slate-400">
          Usando protocolo institucional predeterminado.
        </p>
      )}

      {/* SIGUIENTE ACCIÓN — bloque DOMINANTE (§8/§9). Autoritativa: derivada del backend.
          v2.9.10 (A4): NO se muestra si la recomendación sólo repite un control primario ya visible
          (Tomar/Cerrar). Se decide por kind/semántica, NUNCA por texto. Las acciones que aportan
          instrucción operacional real (audio, teléfono, llamar contacto, seguimiento…) SÍ se muestran. */}
      {/* SIGUIENTE ACCIÓN — superficie operacional PRINCIPAL (OPS-01 §9). Autoritativa: derivada del
          backend. Nunca se queda sin herramientas: debajo van los CTAs reales del caso. */}
      {!isCerrada && !isRecibida && !["cerrado", "ninguno"].includes(a.siguiente_accion?.kind) && (
        <div data-testid="v3-siguiente-accion" className="rounded-xl p-4">
          <p className="gv2-action-text flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest"><ArrowRightCircle className="h-3.5 w-3.5" /> Siguiente acción</p>
          <p data-testid="v3-siguiente-accion-texto" className="gv2-strong-text mt-1.5 text-lg font-extrabold leading-snug tracking-tight">{accion}</p>
          {!tec && !readOnly && (
            <SiguienteAccionControl detail={a} onRegistrarAccion={onRegistrarAccion}
              onRegistrarDesenlace={onRegistrarDesenlace} onResolverSeguimiento={onResolverSeguimiento}
              onRegistrarEscalamiento={onRegistrarEscalamiento} onNotaGestion={onNotaGestion} drafts={drafts} busy={busy} />
          )}
          {/* CTAs REALES (OPS-01 §5): la monitorista elige el medio que de verdad usó. */}
          {!tec && !readOnly && (
            <div data-testid="v3-sa-ctas" className="gv2-divider mt-3 flex flex-wrap items-center gap-2 border-t pt-3">
              {!esRecordatorio && <AudioTwoWay caso={a} demo={demo} readOnly={readOnly} />}
              {a.persona_telefono && (
                <CallBtns telefono={a.persona_telefono} idx="persona" nombre={persona.nombre_completo || a.persona_nombre} secundario
                  onIniciar={() => onIntentoLlamada && onIntentoLlamada({ kind: "llamada_persona", telefono: a.persona_telefono })} />
              )}
              {!esRecordatorio && (
                <button data-testid="v3-cta-otra-via" onClick={() => setDesenlaceOpen(true)}
                  className="inline-flex items-center rounded-lg border border-[#012472]/30 dark:border-[#80B3FF]/40 px-3 py-1.5 text-sm font-bold text-[#012472] dark:text-[#80B3FF] hover:bg-[#012472]/5 transition-colors">
                  Registrar por otra vía
                </button>
              )}
              {/* ATN-01 · M7 — el control PRINCIPAL de cierre vive en el encabezado del caso
                  (`v3-btn-cerrar`), disponible en todas las ramas. Aquí ya no se duplica. */}
              {(a.recursos_disponibles || {}).audio && !esRecordatorio && !instMode && (
                <span data-testid="v3-orden-canales" className="text-xs text-slate-500 dark:text-slate-400">
                  Primero habla por el dispositivo; si no responde, llama al teléfono.
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* v2.13.1 — RECORDATORIO PROGRAMADO: el origen del evento es la PROGRAMACIÓN. Se muestra el
          texto del recordatorio, la HORA OBJETIVO (nunca falseada por el aviso anticipado), la
          anticipación configurada y la hora real de generación operacional. */}
      {esRecordatorio && (
        <div data-testid="v3-recordatorio" className="rounded-xl border-2 border-[#012472]/25 bg-[#012472]/[0.04] dark:bg-[#01CBE3]/[0.06] p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#0891A6] dark:text-[#01CBE3]">Recordatorio programado</p>
          {a.recordatorio ? (
            <>
              <p data-testid="v3-recordatorio-texto" className="mt-1 text-lg font-extrabold tracking-tight text-[#012472] dark:text-white">
                {a.recordatorio.texto}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600 dark:text-slate-300">
                {a.recordatorio.hora_objetivo_local && <span data-testid="v3-recordatorio-hora-objetivo">Hora objetivo: <b className="tabular-nums">{a.recordatorio.hora_objetivo_local}</b></span>}
                {a.recordatorio.anticipacion_min != null && <span data-testid="v3-recordatorio-anticipacion">Anticipación: {a.recordatorio.anticipacion_min} min</span>}
                <span data-testid="v3-recordatorio-generada">Generada: {fmtTimestamp(a.hora_recepcion_servidor)}</span>
                {a.recordatorio.origen_indicacion && <span>Indicación de: {a.recordatorio.origen_indicacion}</span>}
              </div>
            </>
          ) : (
            <p data-testid="v3-recordatorio-sin-detalle" className="mt-1 text-sm font-semibold text-slate-600 dark:text-slate-300">
              Sin detalle del recordatorio
            </p>
          )}
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Galarya comunica el recordatorio. No afirma que el medicamento se tomó, ni interpreta adherencia.
          </p>
        </div>
      )}

      {/* v2.9.4 — REGISTRAR DESENLACE (canal-agnóstico). DESACOPLADO de siguiente_accion: se muestra
          SIEMPRE para todo caso de PERSONA abierto/en atención/no terminal, sea cual sea el kind. La
          siguiente acción de arriba queda como RECOMENDACIÓN; aquí el operador documenta honestamente
          el desenlace si ya resolvió por otra vía. El cierre lo sigue gobernando el backend.
          v2.13.1: en un recordatorio NO hay verificación de estado que documentar -> no se muestra. */}
      {!tec && !esRecordatorio && !isRecibida && !isCerrada && !readOnly && (() => {
        const desHecho = !!(a.verificacion_desenlace || {}).codigo;
        // v2.9.10 (A2): fallback DISCRETO — no compite con los chips. Se abre por acción explícita
        // del operador; EXCEPTO cuando la siguiente acción requerida ES el desenlace (auto-abre). Si
        // ya hay desenlace, el control muestra su RESUMEN read-only.
        const abrir = desHecho || desenlaceOpen || a.siguiente_accion?.kind === "desenlace";
        // OPS-01 §18 — el acceso vive en los CTAs de Siguiente Acción: aquí ya no hay un enlace
        // duplicado compitiendo por el centro del flujo.
        return abrir ? (
          // CORRECCIÓN F — el control se MONTA por (caso, actor): si el operador pasa a otro caso,
          // el control anterior se desmonta y la respuesta tardía de aquel caso ya no puede tocar
          // las selecciones del caso nuevo.
          <DesenlaceDirectoControl key={`desenlace::${a.id}::${actorId || "—"}`} casoId={a.id}
            detail={a} onRegistrarDesenlace={onRegistrarDesenlace} busy={busy} />
        ) : null;
      })()}

      {/* WAITS estructuradas (§6) — compromiso con reloj/vencimiento. Vencer NO cierra el caso. */}
      {!isCerrada && waitsActivas.length > 0 && (
        <Section icon={History} title="Esperas activas" testId="v3-waits" tone="text-[#0891A6] dark:text-[#01CBE3]">
          <ul className="space-y-2">
            {waitsActivas.map((w) => (
              <li key={w.wait_id} data-testid={`v3-wait-${w.wait_id}`}
                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${w.status === "vencida" ? "border-[#FE6A55]/60 bg-[#FE6A55]/10" : "border-slate-200 bg-white"}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-100">{(w.contacto && w.contacto.nombre) || "Contacto"} · {(w.compromiso || "").replace(/_/g, " ")}</p>
                  <p className={`text-xs font-mono tabular-nums ${w.status === "vencida" ? "font-bold text-[#B93B29] dark:text-[#FE6A55]" : "text-slate-500 dark:text-slate-400"}`}>
                    {w.status === "vencida" ? "VENCIÓ" : "Vence"} {fmtTimestamp(w.vence)}
                  </p>
                </div>
                {!readOnly && onCumplirWait && (
                  <Button data-testid={`v3-wait-cumplir-${w.wait_id}`} variant="outline" disabled={busy} onClick={() => onCumplirWait(w.wait_id)}
                    className="shrink-0 border-emerald-400 text-emerald-700 dark:text-emerald-300 dark:border-emerald-600">Confirmó</Button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Cierre y gestión — reconstrucción del caso cerrado (quién, cuándo, resultado, nota) */}
      {isCerrada && (
        <Section icon={Check} title="Cierre y gestión" testId="v3-cierre" tone="text-emerald-700 dark:text-emerald-300">
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-slate-400">Resultado</dt><dd className="font-medium text-slate-700 dark:text-slate-200">{RESULTADO_LABEL[a.clasificacion] || a.clasificacion || "—"}</dd></div>
            {a.causa_falsa && <div className="flex justify-between gap-3"><dt className="text-slate-400">Causa</dt><dd className="text-slate-700 dark:text-slate-200">{a.causa_falsa.replace(/_/g, " ")}</dd></div>}
            <div className="flex justify-between gap-3"><dt className="text-slate-400">Cerrada</dt><dd className="font-mono text-slate-700 dark:text-slate-200">{fmtTimestamp(a.cierre_en)}</dd></div>
            {(a.cerrada_por_nombre || a.cierre_por) && <div className="flex justify-between gap-3"><dt className="text-slate-400">Cerró</dt><dd data-testid="v3-cierre-actor" className="text-slate-700 dark:text-slate-200">{a.cierre_actor_tipo === "sistema" ? "Sistema (recuperación)" : (a.cerrada_por_nombre || a.cierre_por)}</dd></div>}
          </dl>
          {a.nota_cierre
            ? <p data-testid="v3-nota-cierre" className="mt-2.5 rounded-lg bg-slate-50 p-2.5 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{a.nota_cierre}</p>
            : <p className="mt-2.5 text-sm text-slate-400">Sin nota de cierre.</p>}
        </Section>
      )}

      {/* Secuencia del caso — read-model AUTORITATIVO del backend `secuencia_combinada` (v2.9.1).
          Ordena recepción/toma/audio/llamadas/resultados/waits/retrigger/emergencia/cierre. NO se
          reconstruye client-side ni se usa `transiciones[]` como fuente. La Bitácora general sigue
          mostrando los eventos de audio de forma independiente (no se elimina). */}
      {(a.secuencia_combinada || []).length > 0 && (
        <Section icon={History} title="Secuencia del caso" testId="v3-secuencia">
          <ol data-testid="v3-secuencia-lista" className="space-y-2.5">
            {a.secuencia_combinada.map((it, i) => {
              const label = etiquetaHecho(it);
              const d = it.datos || {};
              return (
                <li key={i} data-testid={`v3-secuencia-item-${i}`} className="flex items-start gap-3">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#01CBE3]" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{label}</p>
                    {/* DATA-006: resultado + motivo + nota del MISMO hecho, consultables al reabrir. */}
                    {it.tipo === "gestion_contacto" ? (
                      <p data-testid={`v3-secuencia-gestion-${i}`} className="text-xs text-slate-500 dark:text-slate-400 whitespace-pre-wrap">
                        {d.contacto || "contacto"}{d.relacion ? ` (${d.relacion})` : ""}: <span className="font-semibold">{RES_LBL[d.resultado] || d.resultado}</span>
                        {d.motivo ? ` · motivo: ${MOTIVO_LBL[d.motivo] || d.motivo}` : ""}
                        {d.nota ? ` · ${d.nota}` : ""}
                      </p>
                    ) : it.tipo === "escalamiento_interno" ? (
                      /* GC94 · R02: REGISTRÓ ≠ ATENDIÓ y «solicitado» no es «recibido». Los tres
                         datos persistidos se leen por separado; el personal no se autocompleta. */
                      <p data-testid={`v3-secuencia-escalamiento-${i}`} className="text-xs text-slate-500 dark:text-slate-400 whitespace-pre-wrap">
                        <span className="font-semibold">{String(d.categoria || it.detalle || "").replace(/_/g, " ")}</span>
                        {d.estado_apoyo ? ` · ${ESTADO_APOYO_LABEL[d.estado_apoyo] || d.estado_apoyo}` : ""}
                        {` · Personal consignado: ${d.personal_atendio || "no consta"}`}
                        {d.nota ? ` · ${d.nota}` : ""}
                      </p>
                    ) : (it.resultado_codigo || it.resultado_texto_factual || it.detalle) ? (
                      /* ATN-01 · M2 — el código de resultado y la NOTA FACTUAL se leen POR
                         SEPARADO: una captura conjunta deja de ocultar la nota. La nota es texto
                         literal (React la escapa): nunca se capitaliza, traduce ni se le quitan
                         los guiones bajos. Histórico sin los campos nuevos: fallback compatible. */
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {identidadContacto(it.ref) && (it.tipo === "journal_llamada_contacto" || it.tipo === "journal_aviso_contacto") ? (
                          <span data-testid={`v3-secuencia-contacto-${i}`} className="block font-medium text-slate-600 dark:text-slate-300">{identidadContacto(it.ref)}</span>
                        ) : null}
                        {it.resultado_codigo ? (
                          <p data-testid={`v3-secuencia-resultado-${i}`}>
                            {(it.tipo === "journal_llamada_contacto" || it.tipo === "journal_aviso_contacto") ? "Resultado: " : ""}
                            {etiquetaResultado(it.resultado_codigo)}
                          </p>
                        ) : null}
                        {it.resultado_texto_factual ? (
                          <p data-testid={`v3-secuencia-nota-${i}`} className="whitespace-pre-wrap">{it.resultado_texto_factual}</p>
                        ) : null}
                        {!it.resultado_codigo && !it.resultado_texto_factual && it.detalle ? (
                          it.tipo === "journal_nota" ? (
                            <p data-testid={`v3-secuencia-nota-${i}`} className="whitespace-pre-wrap">{it.detalle}</p>
                          ) : (
                            <p>
                              {(it.tipo === "journal_llamada_contacto" || it.tipo === "journal_aviso_contacto") ? "Resultado: " : ""}
                              {etiquetaResultado(it.detalle)}
                            </p>
                          )
                        ) : null}
                        {it.enlace_llamada_estado === "invalido" && (
                          <p data-testid={`v3-secuencia-enlace-invalido-${i}`} className="text-amber-700 dark:text-amber-400">
                            El intento al que hace referencia este resultado no consta en el caso: no acredita una llamada adicional.
                          </p>
                        )}
                      </div>
                    ) : null}
                    <p className="text-[11px] text-slate-400 tabular-nums font-mono">
                      {it.clase === "evidencia"
                        ? `dispositivo${(it.origen || {}).documentado_por ? ` · documentada por ${it.origen.documentado_por}` : ""}`
                        : (it.actor || "—")} · {fmtTimestamp(it.hora)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </Section>
      )}

      {/* Excepción reportada por el radar (EKH vitals_abnormal) — evidencia LITERAL del evento.
          Copy NEUTRAL, sin nombres internos ni diagnóstico. Lectura del EVENTO, separada de current_state. */}
      {a.ekh_evento && (() => {
        const esResp = a.tipo === "excepcion_frecuencia_respiratoria";
        const hr = a.ekh_evento.heart_rate_evento;
        const rr = a.ekh_evento.respiratory_rate_evento;
        return (
          <Section icon={esResp ? Wind : HeartPulse} title="Excepción reportada por el radar" testId="v3-ekh-evento">
            {esResp && Number.isFinite(rr) ? (
              <p data-testid="v3-ekh-lectura-evento" className="rounded-lg bg-[#012472]/[0.05] dark:bg-[#01CBE3]/[0.08] px-3 py-2 text-sm font-semibold text-[#012472] dark:text-[#80B3FF]">
                {rr} rpm al momento del evento
              </p>
            ) : !esResp && Number.isFinite(hr) ? (
              <p data-testid="v3-ekh-lectura-evento" className="rounded-lg bg-[#012472]/[0.05] dark:bg-[#01CBE3]/[0.08] px-3 py-2 text-sm font-semibold text-[#012472] dark:text-[#80B3FF]">
                {hr} lpm al momento del evento
              </p>
            ) : (
              <p className="text-sm text-slate-700 dark:text-slate-200">
                {esResp ? "Alerta de frecuencia respiratoria reportada por el radar." : "Alerta de frecuencia cardiaca reportada por el radar."}
              </p>
            )}
            <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-400">
              Valor del evento (no es la última lectura actual). Galarya no interpreta clínicamente.
            </p>
          </Section>
        );
      })()}

      {/* Evidencia (sólo alarmas de persona nacidas de un DISPOSITIVO).
          v2.13.3: un recordatorio programado NO nace de un sensor, así que no tiene evidencia que
          buscar; mostrar el marco vacío («Origen: radar (sin cámara)») lo presentaba como un evento
          de radar y era falso. */}
      {!tec && !esRecordatorio && <Evidencia detail={a} demo={demo} />}

      {/* LAB7 · P1-B/C — contexto de la habitación compartida e identificación humana. */}
      {instMode && !tec && (
        <HabitacionCompartida detail={a} onIdentificarResidente={onIdentificarResidente}
          busy={busy} readOnly={readOnly} />
      )}

      {/* Protocolo de referencia (OPS-01 §10): tracker READ-ONLY y colapsable. Dejó de ser un
          checklist manual que duplicaba lo que la monitorista realmente hizo. */}
      <Section icon={ListChecks} title="Protocolo de referencia" testId="v3-plan">
        {!tec && (isCerrada || readOnly ? (
          /* OPS-01 §16 — histórico HONESTO: sólo lo que consta en el caso; jamás la configuración
             actual del sitio presentada como pasado. */
          <div data-testid="v3-metodos-historicos" className="mb-3 space-y-1 rounded-lg bg-slate-50 dark:bg-[#192C47] p-2.5 text-xs">
            <p className="font-semibold uppercase tracking-wide text-slate-400">Métodos que constan en este caso</p>
            {(a.metodos_historicos || []).map((m) => (
              <p key={m.codigo} data-testid={`v3-metodo-hist-${m.codigo}`} className={m.consta ? "text-slate-600 dark:text-slate-300" : "text-slate-400"}>
                · {m.etiqueta}: {m.consta ? (m.detalle || "Consta en el caso.") : "No consta en el caso."}
              </p>
            ))}
            {(a.metodos_historicos || []).length === 0 && (
              <p className="text-slate-400">No consta en el caso información de métodos.</p>
            )}
            <p className="pt-1 text-[11px] text-slate-400">Reconstruido con la evidencia registrada en el caso.</p>
          </div>
        ) : (
          <div data-testid="v3-recursos" className="mb-3 space-y-1 rounded-lg bg-slate-50 dark:bg-[#192C47] p-2.5 text-xs">
            <p className="font-semibold uppercase tracking-wide text-slate-400">{instMode ? "Con qué responde el centro" : "Métodos disponibles en este sitio"}</p>
            {instMode ? (
              <>
                <p className="text-slate-600 dark:text-slate-300">· Verificación presencial del personal del centro: vía estándar.</p>
                <p className="text-slate-500 dark:text-slate-400">{rec.audio ? "· Audio con la habitación disponible como apoyo (no sustituye la verificación presencial)." : `· Audio con la habitación ${audioTxt} No es la vía estándar en el centro.`}</p>
                <p className="text-slate-500 dark:text-slate-400">{rec.num_contactos > 0 ? `· ${rec.num_contactos} contacto(s) de familia para aviso posterior.` : "· Sin contactos de familia registrados."}</p>
              </>
            ) : (
              <>
                <p className={rec.audio ? "text-slate-600 dark:text-slate-300" : "text-amber-600 dark:text-amber-400"}>
                  {rec.audio ? "· Verificación por audio disponible." : `· Verificación por audio ${audioTxt}`}
                </p>
                <p className={rec.telefono_persona ? "text-slate-600 dark:text-slate-300" : "text-amber-600 dark:text-amber-400"}>
                  {rec.telefono_persona ? "· Teléfono de la persona registrado." : "· La persona no tiene teléfono registrado."}
                </p>
                <p className={rec.num_contactos > 0 ? "text-slate-600 dark:text-slate-300" : "text-amber-600 dark:text-amber-400"}>
                  {rec.num_contactos > 0 ? `· ${rec.num_contactos} contacto(s) de escalamiento.` : "· No hay contactos de escalamiento configurados."}
                </p>
                <p className="text-slate-500 dark:text-slate-400">
                  {rec.verificador_contratado ? "· Verificador contratado en sitio." : "· Este sitio no tiene verificador contratado."}
                </p>
              </>
            )}
          </div>
        ))}
        {(() => {
          // LAB7 · P1-B — el protocolo que gobierna la presentación viene del backend CON su
          // fuente declarada. Nunca se afirma «no existe protocolo» si la ruta sí está definida,
          // y un caso cerrado sin snapshot NO reconstruye pasos.
          const pe = a.protocolo_efectivo || null;
          const lista = pe ? (pe.pasos || []) : pasos;
          const hechos = lista.filter((p) => p.ejecutado).length;
          if (lista.length === 0 && !pe?.plan) {
            return (
              <div data-testid="v3-sin-plan" className="space-y-2.5">
                <p data-testid="v3-protocolo-sin-pasos" className="text-sm text-slate-500 dark:text-slate-300">
                  {pe?.aviso || (pe?.sin_reconstruccion
                    ? "No consta un protocolo congelado en este caso."
                    : "No existe protocolo definido para este tipo de evento.")}
                </p>
              </div>
            );
          }
          return (
            <>
              {pe?.fuente_label && (
                <p data-testid="v3-protocolo-fuente" data-fuente={pe.fuente}
                  className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {pe.fuente_label}
                </p>
              )}
              {pe?.aviso && <p data-testid="v3-protocolo-aviso" className="mb-2 text-xs text-slate-500 dark:text-slate-400">{pe.aviso}</p>}
              {pe?.plan && (
                <div data-testid="v3-protocolo-plan" className="mb-2 rounded-lg bg-slate-50 dark:bg-[#192C47] p-2.5">
                  {pe.plan.instrucciones_especiales && (
                    <p data-testid="v3-plan-instrucciones" className="text-sm text-slate-700 dark:text-slate-200">{pe.plan.instrucciones_especiales}</p>
                  )}
                  {pe.plan.decision_emergencia && (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Decisión de emergencia: {pe.plan.decision_emergencia}</p>
                  )}
                  {(pe.plan.familias_activas || []).length > 0 && (
                    <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-400">Familias activas: {pe.plan.familias_activas.join(" · ")}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-400">Información del plan. No son pasos a ejecutar.</p>
                </div>
              )}
              {lista.length > 0 && (
                <button data-testid="v3-plan-toggle" onClick={() => setPlanOpen(!planOpen)}
                  className="text-sm font-semibold text-[#0891A6] dark:text-[#01CBE3] underline underline-offset-2 hover:text-[#012472] dark:hover:text-white transition-colors">
                  {planOpen ? "Ocultar protocolo" : `Ver protocolo · ${hechos} de ${lista.length} paso(s) con registro`}
                </button>
              )}
              {lista.length === 0 && (
                <p data-testid="v3-protocolo-sin-pasos" className="text-sm text-slate-500 dark:text-slate-300">
                  {pe?.sin_reconstruccion ? "No consta una secuencia de pasos en este caso." : "Sin pasos operativos para este caso."}
                </p>
              )}
              {planOpen && lista.length > 0 && (
                <ol data-testid="v3-plan-pasos" className="mt-3 space-y-2.5">
                  {lista.map((p) => (
                    <li key={p.orden} data-testid={`v3-paso-${p.orden}`} className="flex items-start gap-3">
                      <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${p.ejecutado ? "bg-[#01CBE3]/20 text-[#0891A6] dark:text-[#01CBE3]" : "bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-300"}`}>
                        {p.ejecutado ? "Realizado" : "Pendiente"}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-slate-700 dark:text-slate-200">{p.texto}</p>
                        {p.nota_sitio && <p data-testid={`v3-paso-nota-${p.orden}`} className="text-xs text-slate-500 dark:text-slate-400">Nota del centro: {p.nota_sitio}</p>}
                        {p.ejecutado && p.ejecutado_por && <p className="text-xs text-slate-400 font-mono">{p.ejecutado_por} · {fmtTimestamp(p.ejecutado_en)}</p>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              <p className="mt-2 text-[11px] text-slate-400">Referencia del protocolo. El avance se registra con las acciones y resultados reales del caso, no marcando casillas.</p>
            </>
          );
        })()}
      </Section>

      {/* Cuidadora + contactos (persona) */}
      {!tec && cui && (
        <Section icon={UserCog} title={instMode ? "Responsable de turno del centro" : "Cuidadora en turno"} testId="v3-cuidadora">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-slate-800 dark:text-slate-100">{cui.nombre}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">{instMode ? (cui.rol || "Personal del centro") : cui.tipo === "planta" ? "De planta (24/7)" : "Por horas"} · {cui.telefono}</p>
            </div>
            <CallBtns telefono={cui.telefono} idx="cuidadora" bloqueado={isRecibida} />
          </div>
        </Section>
      )}
      {!tec && (
        <Section icon={ShieldAlert} title={instMode ? "Familia y contactos externos · escalamiento secundario" : "Contactos en orden de escalamiento"} testId="v3-contactos">
          {instMode && <p data-testid="v3-contactos-inst-nota" className="mb-3 text-xs text-slate-500 dark:text-slate-400">En el centro la primera línea de respuesta es el personal asignado. Estos contactos se avisan después, cuando el protocolo del centro lo indica.</p>}
          {contactos.length === 0 ? <p data-testid="v3-sin-contactos" className="text-sm text-amber-600 dark:text-amber-400">No hay contactos de escalamiento configurados.</p> : (
            <ul className="space-y-3">
              {contactos.map((rel, i) => {
                const cid = rel.contacto_id || rel.contacto?.id;
                const uh = ultimoHechoContacto(cid);
                const ug = uh && uh.fuente === "gestion" ? uh.g : null;
                const hj = uh && uh.fuente === "journal" ? uh.it : null;
                return (
                <li key={i} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 dark:text-slate-100"><span className="font-mono text-slate-400 mr-1.5">{rel.orden || i + 1}.</span>{rel.contacto?.nombre_completo || "—"}<span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">{rel.relacion_familiar}</span></p>
                      {rel.autoridad && <p className="text-xs text-slate-500 dark:text-slate-400">{rel.autoridad}</p>}
                    </div>
                    <CallBtns telefono={rel.contacto?.telefono} idx={i} nombre={rel.contacto?.nombre_completo} bloqueado={isRecibida} />
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    {ug ? (
                      <p data-testid={`v3-ultima-gestion-${i}`} className="min-w-0 text-xs text-slate-500 dark:text-slate-400">
                        Contacto con <span className="font-medium text-slate-600 dark:text-slate-300">{ug.contacto_snapshot?.nombre || rel.contacto?.nombre_completo}{ug.contacto_snapshot?.relacion ? ` (${ug.contacto_snapshot.relacion})` : ""}</span>: <span className="font-semibold text-slate-700 dark:text-slate-200">{RES_LBL[ug.resultado] || ug.resultado}</span>
                        {ug.motivo ? <> · motivo: <span className="font-medium">{MOTIVO_LBL[ug.motivo] || ug.motivo}</span></> : null}
                        {" "}· {ug.registrado_por} · {fmtTimestamp(ug.registrado_en)}
                        {ug.nota ? <span data-testid={`v3-ultima-gestion-nota-${i}`} className="block whitespace-pre-wrap text-slate-500 dark:text-slate-400">{ug.nota}</span> : null}
                      </p>
                    ) : hj ? (
                      <p data-testid={`v3-ultima-gestion-${i}`} className="min-w-0 text-xs text-slate-500 dark:text-slate-400">
                        {hj.fase === "iniciada" ? (
                          <>Llamada <span className="font-semibold text-slate-700 dark:text-slate-200">iniciada</span>, sin resultado registrado</>
                        ) : (
                          <>Resultado: <span className="font-semibold text-slate-700 dark:text-slate-200">{RESULTADO_LABEL[hj.detalle] || String(hj.detalle || "registrado").replace(/_/g, " ")}</span></>
                        )}
                        {" "}· {hj.actor || "—"} · {fmtTimestamp(hj.hora)}
                      </p>
                    ) : <span className="text-xs text-slate-400">Sin gestión registrada</span>}
                    {onRegistrarGestion && !readOnly && !isCerrada && cid && (
                      <button data-testid={`v3-registrar-gestion-${i}`}
                        aria-label={`Registrar el resultado del contacto con ${rel.contacto?.nombre_completo || `contacto ${rel.orden || i + 1}`}`}
                        onClick={(ev) => { disparadorGestionRef.current = ev.currentTarget; setGestionFor({ contacto_id: cid, nombre: rel.contacto?.nombre_completo, relacion: rel.relacion_familiar }); }}
                        className="shrink-0 rounded-lg border border-[#012472]/30 px-2.5 py-1 text-xs font-semibold text-[#012472] dark:text-[#80B3FF] hover:bg-[#012472]/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#012472]">
                        Registrar resultado
                        <span className="sr-only"> del contacto con {rel.contacto?.nombre_completo || `contacto ${rel.orden || i + 1}`}</span>
                      </button>
                    )}
                  </div>
                </li>
              );})}
            </ul>
          )}
        </Section>
      )}

      {/* Notas de la gestión — persistidas para TODO caso (autor+hora backend, sobreviven refresh/relevo).
          Persona conserva además un borrador local que precarga el cierre. */}
      {/* NOTAS — acción discreta/opcional (OPS-01 §11). El registro permanente se conserva; la
          captura rutinaria duplicada desaparece del centro del flujo. */}
      {((a.notas_gestion || []).length > 0 || (!isCerrada && onNotaGestion && !readOnly)) && (
        <Section icon={StickyNote} title={isCerrada ? "Notas de la gestión (registro)" : "Notas de la gestión"} testId="v3-notas-gestion">
          {(a.notas_gestion || []).length > 0 && (
            <ul data-testid="v3-lista-notas" className="space-y-2 mb-3">
              {a.notas_gestion.map((n, i) => (
                <li key={i} className="rounded-lg border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-[#192C47] p-2.5">
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{n.texto}</p>
                  <p className="mt-1 text-[11px] text-slate-400 tabular-nums">{n.por} · {fmtTimestamp(n.en)}</p>
                </li>
              ))}
            </ul>
          )}
          {!isCerrada && onNotaGestion && !readOnly && (
            notaOpen ? (
              <NotaGestionV3 onGuardar={async (t) => { const r = await onNotaGestion(t); if (!r || r.estado === undefined || String(r.estado).startsWith("confirmado")) setNotaOpen(false); return r; }} busy={busy}
                valor={drafts ? drafts.get(a.id, "nota_gestion") : undefined}
                onCambio={drafts ? ((v) => drafts.set(a.id, "nota_gestion", v)) : undefined} />
            ) : (
              <button data-testid="v3-btn-agregar-nota" onClick={() => setNotaOpen(true)}
                className="text-sm font-semibold text-[#0891A6] dark:text-[#01CBE3] underline underline-offset-2 hover:text-[#012472] dark:hover:text-white transition-colors">
                Agregar nota
              </button>
            )
          )}
        </Section>
      )}

      {/* Timeline reciente (sólo alarmas de persona) */}
      {!tec && (
      <Section icon={History} title="Eventos recientes de esta persona" testId="v3-historial">
        {(!a.historial || a.historial.length === 0) ? <p className="text-sm text-slate-400">Sin eventos previos.</p> : (
          <ul className="space-y-2">
            {a.historial.map((h) => (
              <li key={h.id} className="flex items-center justify-between text-sm">
                <span className="text-slate-700 dark:text-slate-200">{tipoMeta(h.tipo).label}</span>
                <span className="text-slate-400 font-mono">{fmtTimestamp(h.hora_recepcion_servidor)}{h.clasificacion ? ` · ${RESULTADO_LABEL[h.clasificacion] || h.clasificacion}` : ` · ${ESTADO_LABEL[h.estado] || h.estado}`}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      )}
      {onRegistrarGestion && (
        <RegistrarResultadoModal open={!!gestionFor} onOpenChange={(v) => { if (!v) { setGestionFor(null); devolverFocoGestion(); } }}
          contacto={gestionFor} submitting={busy}
          onConfirm={async (payload) => {
            const origen = gestionFor;
            const r = await onRegistrarGestion(origen.contacto_id, payload);
            const confirmado = !r || !r.estado || String(r.estado).startsWith("confirmado");
            // REPARACIÓN M6-C/F — el cierre visual se aplica SÓLO al caso y contacto que originaron
            // el envío: una respuesta tardía no cierra el formulario de otro caso.
            const mismoOrigen = (!r?.casoId || r.casoId === a.id)
              && (!r?.contactoId || r.contactoId === origen.contacto_id);
            if (confirmado && mismoOrigen) { setGestionFor(null); devolverFocoGestion(); }
            return r;
          }} />
      )}
    </div>
  );
}
